import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
//service
import { CurrencyDecimalFormat } from "../../helper";
import style from "./ewallet.module.scss";

const SkeletonRow = () => (
  <div style={{ display: "flex", gap: "16px", padding: "12px 16px", borderBottom: "1px solid #f0f0f0", alignItems: "center" }}>
    {[30, 15, 20, 15].map((w, i) => (
      <div key={i} style={{ width: `${w}%`, height: "14px", borderRadius: "4px", background: "linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%)", backgroundSize: "200% 100%", animation: "skeletonShimmer 1.2s infinite" }} />
    ))}
  </div>
);

const TableSkeleton = () => (
  <div>
    <style>{`@keyframes skeletonShimmer { 0% { background-position: 200% 0 } 100% { background-position: -200% 0 } }`}</style>
    {Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)}
  </div>
);

const SpendingWalletTable = React.memo(({ ContentData, paginationchange, filterchange }) => {
  const { t } = useTranslation();
  const Currency = useSelector((state) => state.curr);
  const [direction, setDirection] = useState(ContentData.direction || "");

  const handleDirectionFilter = (e) => {
    const val = e.target.value;
    setDirection(val);
    if (filterchange) filterchange(val);
  };

  // Translate the langCode, but fall back to the backend-provided plain
  // description when the key is missing, so a row never renders as
  // "ewallet.undefined".
  const buildDescription = (resData) => {
    const langCode = resData.description1?.langCode;
    if (!langCode) {
      return resData.description || "";
    }
    const key = "ewallet." + langCode;
    const translated = t(key, { username: resData.description1?.user });
    return translated === key ? resData.description || "" : translated;
  };

  const columns = [
    {
      name: t("Common.description").toUpperCase(),
      selector: (row) => row.description,
      width: "30%",
    },
    {
      name: t("Common.amount").toUpperCase(),
      selector: (row) => row.amount,
      cell: (row) => (
        <span className={`${row.classnameamount} ${style.badge}`}>
          {Currency.currentCurr}{" "}
          {CurrencyDecimalFormat(
            row.amount * Currency.value,
            Currency.precision
          )}
        </span>
      ),
    },
    {
      name: t("Common.transactionDate").toUpperCase(),
      selector: (row) => row.transaction_date,
      sortable: true,
    },
    {
      name: t("Common.balance").toUpperCase(),
      selector: (row) => row.balance,
      cell: (row) => (
        <span className={`${style.balanceAmount} ${style.badge}`}>
          {Currency.currentCurr}{" "}
          {(row.balance * Currency.value).toFixed(Currency.precision)}
        </span>
      ),
    },
  ];

  const [state, setState] = useState({ data: [] });
  const { data } = state;

  useEffect(() => {
    if (ContentData.data) {
      let NewData = ContentData.data.map((resData) => {
        if (!resData) return null;
        return {
          classnameamount:
            resData.type === "debit" ? style.debit : style.credit,
          amount: resData.amount,
          description: buildDescription(resData),
          transaction_date: resData.transaction_date,
          balance: resData.balance,
        };
      });
      setState((prev) => ({
        ...prev,
        data: NewData,
      }));
    }
  }, [ContentData]);

  const handlePerRowsChange = (perPage, inPage) => {
    paginationchange(inPage, perPage);
  };
  const handlePageChange = (inPage) => {
    paginationchange(inPage, ContentData.perPage);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
        <select
          value={direction}
          onChange={handleDirectionFilter}
          style={{ padding: "6px 12px", borderRadius: "4px", border: "1px solid #ccc" }}
        >
          <option value="">{t("Common.all")}</option>
          <option value="credit">{t("Common.credit")}</option>
          <option value="debit">{t("Common.debit")}</option>
        </select>
      </div>
      {ContentData.pending ? (
        <TableSkeleton />
      ) : (
        <DataTable
          columns={columns}
          data={data}
          highlightOnHover
          persistTableHead
          noHeader
          pagination
          paginationServer
          responsive
          paginationTotalRows={ContentData.totalRows}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          noDataComponent={t("Common.noDataRecordsToDisplay")}
        />
      )}
    </div>
  );
});

export default SpendingWalletTable;
